import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang, useT } from '../i18n/LanguageContext';
import { tenantAPI } from '../services/api';
import Icon from './Icon';

/** Initials for the avatar from a username/email. */
function initials(name: string | null): string {
  if (!name) return '?';
  const base = name.includes('@') ? name.split('@')[0] : name;
  const parts = base.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  const chars = parts.slice(0, 2).map((p) => p[0] || '');
  return (chars.join('') || base[0] || '?').toUpperCase();
}

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username, role, logout } = useAuth();
  const { lang, setLang } = useLang();
  const t = useT();

  const isAdmin = role === 'super_admin' || role === 'tenant_admin';
  const path = location.pathname;

  // Whether the sent-messages overview is available to this user. super_admin
  // always; other roles only when the admin enabled it for their tenant.
  const [canSeeMessages, setCanSeeMessages] = useState(role === 'super_admin');
  useEffect(() => {
    if (role === 'super_admin') { setCanSeeMessages(true); return; }
    tenantAPI.getMySender()
      .then((res) => setCanSeeMessages(!!res.data?.data?.messagesOverview))
      .catch(() => setCanSeeMessages(false));
  }, [role]);

  // Active nav item mapping (campaignDetail/builder -> Kampanje, listDetail -> Liste).
  const active = (test: (p: string) => boolean) => (test(path) ? 'navlink active' : 'navlink');

  // --- theme toggle (light/dark) — tokens already support [data-theme="dark"] ---
  const [theme, setTheme] = useState<string>(() => localStorage.getItem('vs-theme') || 'light');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('vs-theme', theme);
  }, [theme]);

  // --- avatar dropdown ---
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <nav className="topbar">
      <div className="brand" onClick={() => navigate('/')}>
        <span className="brand-mark">
          <Icon name="send" size={18} />
        </span>
        <span className="brand-name">
          Vemid <b>Sendly</b>
        </span>
      </div>

      <div className="topnav">
        <button className={active((p) => p === '/')} onClick={() => navigate('/')}>
          <Icon name="dashboard" size={17} />
          <span>{t('Pregled', 'Overview')}</span>
        </button>
        <button className={active((p) => p.startsWith('/campaigns'))} onClick={() => navigate('/campaigns')}>
          <Icon name="megaphone" size={17} />
          <span>{t('Kampanje', 'Campaigns')}</span>
        </button>
        <button className={active((p) => p.startsWith('/contacts'))} onClick={() => navigate('/contacts')}>
          <Icon name="contacts" size={17} />
          <span>{t('Kontakti', 'Contacts')}</span>
        </button>
        <button className={active((p) => p.startsWith('/contact-lists'))} onClick={() => navigate('/contact-lists')}>
          <Icon name="lists" size={17} />
          <span>{t('Liste', 'Lists')}</span>
        </button>
        {canSeeMessages && (
          <button className={active((p) => p.startsWith('/messages'))} onClick={() => navigate('/messages')}>
            <Icon name="inbox" size={17} />
            <span>{t('Pregled poruka', 'Messages')}</span>
          </button>
        )}
        {isAdmin && (
          <button
            className={active((p) => p.startsWith('/admin') && !p.startsWith('/admin/reports'))}
            onClick={() => navigate(role === 'super_admin' ? '/admin/tenants' : '/admin/users')}
          >
            <Icon name="shield" size={17} />
            <span>{t('Administracija', 'Admin')}</span>
          </button>
        )}
        {role === 'super_admin' && (
          <button className={active((p) => p.startsWith('/admin/reports'))} onClick={() => navigate('/admin/reports')}>
            <Icon name="trendUp" size={17} />
            <span>{t('Izveštaji', 'Reports')}</span>
          </button>
        )}
      </div>

      <div className="topbar-right">
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/campaigns/new')}>
          <Icon name="plus" size={16} />
          <span className="new-campaign-label">{t('Nova kampanja', 'New campaign')}</span>
        </button>

        <div className="lang-toggle" role="group" aria-label={t('Jezik', 'Language')}>
          <button className={lang === 'sr' ? 'active' : ''} onClick={() => setLang('sr')}>SR</button>
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>

        <button
          className="icon-btn"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label={theme === 'dark' ? t('Svetla tema', 'Light theme') : t('Tamna tema', 'Dark theme')}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
        </button>

        <button className="icon-btn" aria-label={t('Obaveštenja', 'Notifications')}>
          <Icon name="bell" size={18} />
        </button>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className="avatar"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={t('Korisnički meni', 'User menu')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            {initials(username)}
          </button>
          {menuOpen && (
            <div className="card" role="menu" style={menuStyle}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{username}</div>
                <div className="dim" style={{ fontSize: 12.5, marginTop: 2 }}>{role}</div>
              </div>
              <button className="navlink" style={menuItemStyle} onClick={() => { setMenuOpen(false); navigate('/admin/users'); }}>
                <Icon name="settings" size={17} />
                <span>{t('Podešavanja', 'Settings')}</span>
              </button>
              <button className="navlink" style={menuItemStyle} onClick={handleLogout}>
                <Icon name="logout" size={17} />
                <span>{t('Odjava', 'Sign out')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

const menuStyle: React.CSSProperties = {
  position: 'absolute',
  right: 0,
  top: 'calc(100% + 8px)',
  width: 220,
  padding: 6,
  zIndex: 50,
  boxShadow: 'var(--sh-lg)',
};

const menuItemStyle: React.CSSProperties = {
  width: '100%',
  justifyContent: 'flex-start',
};

export default Navigation;
